import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiExternalLink, FiPlus, FiFileText } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteCustomPage, getAdminCustomPages } from 'apis/adminCustomPages';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const CustomPagesList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? '' : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery(
        'custom-pages',
        queryParams,
        getAdminCustomPages
    );
    const { data: languages = [] } = useActiveLanguagesQuery();
    const deleteMutation = useAdminDeleteMutation('custom-pages', deleteCustomPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter, languageCodeFilter]);

    useEffect(() => {
        if (error) toast.error(error.message || 'Tải dữ liệu thất bại');
    }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) {
                toast.success('Xóa trang thành công');
            } else {
                toast.error(res.message);
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const handleOpenPublicPage = (slug) => {
        if (!slug) return;
        const cleanSlug = slug.replace(/^\/+/, '');
        window.open(`/${cleanSlug}`, '_blank');
    };

    const columns = [
        { field: 'id', header: 'ID', width: '70px' },
        {
            field: 'title',
            header: 'Tiêu đề trang',
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                    <FiFileText size={16} color="#4DB748" />
                    <span>{row.title || `Trang #${row.id}`}</span>
                </div>
            )
        },
        {
            field: 'slug',
            header: 'Đường dẫn (Slug)',
            render: (row) => (
                <span style={{ fontFamily: 'monospace', color: '#0d6efd', backgroundColor: '#f0f7ff', padding: '2px 8px', borderRadius: '4px' }}>
                    /{row.slug?.replace(/^\/+/, '')}
                </span>
            )
        },
        { field: 'sortOrder', header: 'Thứ tự', width: '80px' },
        {
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Tạm ẩn'}
                </span>
            )
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button
                        className="icon-btn"
                        style={{ color: '#0d6efd', borderColor: '#cfe2ff' }}
                        onClick={() => handleOpenPublicPage(row.slug)}
                        title="Xem trang công khai"
                    >
                        <FiExternalLink size={15} />
                    </button>
                    <button
                        className="icon-btn icon-btn--edit"
                        onClick={() => navigate(`/admin/custom-pages/edit/${row.id}`)}
                        title="Sửa"
                    >
                        <FiEdit2 size={15} />
                    </button>
                    <button
                        className="icon-btn icon-btn--delete"
                        onClick={() => setDeleteId(row.id)}
                        title="Xóa"
                    >
                        <FiTrash2 size={15} />
                    </button>
                </div>
            )
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Quản lý Trang (Custom Pages)"
                subtitle="Tạo và quản lý các trang nội dung, chính sách bảo mật, điều khoản sử dụng hoặc bài giới thiệu"
                action={
                    <Button onClick={() => navigate('/admin/custom-pages/create')}>
                        <FiPlus style={{ marginRight: '6px' }} /> Thêm Trang Mới
                    </Button>
                }
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input
                        placeholder="Tìm tiêu đề, slug, nội dung..."
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={e => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Tạm ẩn' }
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={languageCodeFilter}
                        onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={[...languages.map(l => ({ value: l.code, label: l.name }))]}
                    />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{
                    currentPage: data.currentPage,
                    totalPages: Math.ceil(data.total / data.pageSize) || 1,
                    onPageChange: setCurrentPage
                }}
            />
            <ConfirmationDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Xóa Trang"
                message="Bạn có chắc chắn muốn xóa trang này không? Hành động này sẽ không thể hoàn tác."
            />
        </div>
    );
};

export default CustomPagesList;
